import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiDownload, FiEdit2 } from 'react-icons/fi';
import DataTable, { Column } from '@shared/ui/data-table';
import { Button } from '@shared/ui/button';
import { Select, SelectOption } from '@shared/ui/select';
import CustomDatePicker from '@shared/ui/custom-datepicker';
import { useAppDispatch, useAppSelector } from '../../../app/store/store';
import { fetchAllCompanies, selectCompanyList } from '../../../entities/company/model';
import { selectUser } from '../../../entities/auth/model';
import { setErrorNotification } from '../../../app/store/notifications';
import { asList, diagnosticsApi } from '../../../api/services/diagnostics.service';
import { downloadReportBlob } from '../../../api/services/reports.service';
import { formatDate, isDiagnosticsStaff, serialOrNone } from '@entities/diagnostics/lib';
import { DeviceResultModal } from '@features/diagnostic-device-result';
import {
  DEFECT_LABELS,
  DISPOSITION_LABELS,
  RESPONSIBLE_LABELS,
  WARRANTY_LABELS,
} from '../../../types/diagnosticsInterfaces';
import type {
  DeviceModel,
  DiagnosticDevice,
  DiagnosticReportFilters,
  DiagnosticReportRow,
  DiagnosticReportSummary,
  DiagnosticTypeInfo,
} from '../../../types/diagnosticsInterfaces';

const PAGE_SIZE = 50;
const FIELD_LABEL = 'block text-sm font-medium text-[#2d2c2c] mb-1';
const selectControlStyles = { control: () => ({ height: '44px', minHeight: '44px' }) };

interface DateFilters {
  startDate: Date | null;
  endDate: Date | null;
}

const toIsoDate = (d: Date | null | undefined): string | undefined =>
  d ? d.toISOString().slice(0, 10) : undefined;

const enumOptions = <K extends string>(labels: Record<K, string>): SelectOption[] =>
  (Object.entries(labels) as [K, string][])
    .filter(([v]) => v !== 'none' && v !== 'unknown')
    .map(([value, label]) => ({ value, label }));

/** Вкладка «Отчёт»: строки-устройства с фильтрами, сводка, экспорт XLSX. */
export default function DiagnosticsReportTab() {
  const dispatch = useAppDispatch();
  const companies = useAppSelector(selectCompanyList) as { id: number; name: string }[];
  const me = useAppSelector(selectUser);
  const canEdit = isDiagnosticsStaff(me);

  const [types, setTypes] = useState<DiagnosticTypeInfo[]>([]);
  const [models, setModels] = useState<DeviceModel[]>([]);
  const [dates, setDates] = useState<DateFilters>({ startDate: null, endDate: null });
  const [company, setCompany] = useState<SelectOption | null>(null);
  const [type, setType] = useState<SelectOption | null>(null);
  const [model, setModel] = useState<SelectOption | null>(null);
  const [warranty, setWarranty] = useState<SelectOption | null>(null);
  const [defect, setDefect] = useState<SelectOption | null>(null);
  const [party, setParty] = useState<SelectOption | null>(null);
  const [disposition, setDisposition] = useState<SelectOption | null>(null);
  const [serial, setSerial] = useState('');

  const [applied, setApplied] = useState<DiagnosticReportFilters>({});
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<DiagnosticReportRow[]>([]);
  const [count, setCount] = useState(0);
  const [summary, setSummary] = useState<DiagnosticReportSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Строку отчёта можно дозаполнить, не уходя в карточку заявки: открываем ту же
  // модалку результата, что и в карточке (нужен полный объект устройства).
  const [editing, setEditing] = useState<{ ticketId: number; device: DiagnosticDevice } | null>(null);
  const [openingRow, setOpeningRow] = useState<number | null>(null);

  useEffect(() => {
    dispatch(fetchAllCompanies());
    diagnosticsApi.getTypes().then((r) => setTypes(r.data)).catch(() => setTypes([]));
    diagnosticsApi.getDeviceModels().then((r) => setModels(asList(r.data))).catch(() => setModels([]));
  }, [dispatch]);

  const buildFilters = (): DiagnosticReportFilters => ({
    received_from: toIsoDate(dates.startDate),
    received_to: toIsoDate(dates.endDate),
    company: company ? Number(company.value) : '',
    ticket_type: type ? Number(type.value) : '',
    model: model ? [Number(model.value)] : undefined,
    warranty_status: (warranty?.value as DiagnosticReportFilters['warranty_status']) ?? '',
    defect_type: (defect?.value as DiagnosticReportFilters['defect_type']) ?? '',
    responsible_party: (party?.value as DiagnosticReportFilters['responsible_party']) ?? '',
    disposition: (disposition?.value as DiagnosticReportFilters['disposition']) ?? '',
    serial: serial.trim() || undefined,
  });

  const load = useCallback(async (filters: DiagnosticReportFilters, p: number) => {
    setLoading(true);
    try {
      const [list, sum] = await Promise.all([
        diagnosticsApi.getReport(filters, p, PAGE_SIZE),
        diagnosticsApi.getReportSummary(filters),
      ]);
      setRows(list.data.results);
      setCount(list.data.count);
      setSummary(sum.data);
    } catch (e: any) {
      dispatch(setErrorNotification(e?.response?.data?.detail || 'Не удалось загрузить отчёт'));
      setRows([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    load(applied, page);
  }, [applied, page, load]);

  const apply = () => {
    setPage(1);
    setApplied(buildFilters());
  };
  const resetAll = () => {
    setDates({ startDate: null, endDate: null });
    setCompany(null); setType(null); setModel(null);
    setWarranty(null); setDefect(null); setParty(null); setDisposition(null);
    setSerial('');
    setPage(1);
    setApplied({});
  };

  const openRow = async (row: DiagnosticReportRow) => {
    if (!canEdit) return;
    setOpeningRow(row.id);
    try {
      const { data } = await diagnosticsApi.getDevice(row.ticket_id, row.id);
      setEditing({ ticketId: row.ticket_id, device: data });
    } catch {
      dispatch(setErrorNotification('Не удалось открыть устройство'));
    } finally {
      setOpeningRow(null);
    }
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const { data } = await diagnosticsApi.exportReport(applied);
      downloadReportBlob(data, 'diagnostics_report.xlsx');
    } catch {
      dispatch(setErrorNotification('Не удалось выгрузить отчёт'));
    } finally {
      setExporting(false);
    }
  };

  const companyOptions = useMemo(
    () => (companies ?? []).map((c) => ({ value: c.id, label: c.name })).sort((a, b) => a.label.localeCompare(b.label, 'ru')),
    [companies],
  );
  const typeOptions = useMemo(() => types.map((t) => ({ value: t.ticket_type.id, label: t.ticket_type.name })), [types]);
  const modelOptions = useMemo(() => models.map((m) => ({ value: m.id, label: m.name })), [models]);

  const columns: Column<DiagnosticReportRow>[] = useMemo(
    () => [
      {
        key: 'ticket',
        header: 'Заявка',
        stopRowClick: true,
        render: (r) => (
          <Link
            to={`/devices/diagnostics/${r.ticket_id}`}
            className="font-mono text-xs text-blue-700 hover:underline"
            title="Открыть заявку"
          >
            {r.ticket_number ?? `#${r.ticket_id}`}
          </Link>
        ),
      },
      { key: 'product', header: 'Продукт', render: (r) => <span className="font-medium text-[#2d2c2c]">{r.product}</span> },
      { key: 'serial', header: 'Номер', render: (r) => <span className="font-mono text-xs">{serialOrNone(r.serial)}</span> },
      { key: 'received', header: 'Поступил', render: (r) => <span className="text-xs text-gray-600">{formatDate(r.received_at)}</span> },
      { key: 'made', header: 'Дата производства', render: (r) => <span className="text-xs text-gray-600">{r.manufactured_at ? formatDate(r.manufactured_at) : 'Х'}</span> },
      { key: 'client', header: 'Клиент', render: (r) => <span className="text-xs">{r.client || '—'}</span> },
      { key: 'housing', header: 'Корпус', render: (r) => <span className="text-xs text-gray-600">{r.housing_name || '—'}</span> },
      {
        key: 'note',
        header: 'Примечание',
        render: (r) => {
          const parts = [
            r.warranty_status !== 'unknown' ? WARRANTY_LABELS[r.warranty_status] : '',
            r.condition === 'ok' ? 'Исправен' : '',
            r.disposition !== 'none' ? DISPOSITION_LABELS[r.disposition] : '',
          ].filter(Boolean);
          return <span className="text-xs">{parts.join(', ') || '—'}</span>;
        },
      },
      { key: 'reason', header: 'Причина', render: (r) => <span className="text-xs truncate max-w-[220px] inline-block" title={r.reason}>{r.reason || '—'}</span> },
      { key: 'party', header: 'Виновник', render: (r) => <span className="text-xs">{r.responsible_party !== 'none' ? RESPONSIBLE_LABELS[r.responsible_party] : '—'}</span> },
      { key: 'defect', header: 'Брак', render: (r) => <span className="text-xs">{r.defect_type !== 'none' ? DEFECT_LABELS[r.defect_type] : '—'}</span> },
      { key: 'type', header: 'Тип', render: (r) => <span className="text-xs text-gray-500">{r.ticket_type ?? '—'}</span> },
      ...(canEdit
        ? [
            {
              key: 'edit',
              header: '',
              headerClassName: 'w-[1%]',
              stopRowClick: true,
              render: (r: DiagnosticReportRow) => (
                <button
                  type="button"
                  title="Дозаполнить результат"
                  disabled={openingRow === r.id}
                  onClick={() => {
                    void openRow(r);
                  }}
                  className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-gray-500 hover:text-[#2d2c2c] hover:bg-gray-100 disabled:opacity-40"
                >
                  <FiEdit2 />
                </button>
              ),
            } as Column<DiagnosticReportRow>,
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canEdit, openingRow],
  );

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <CustomDatePicker label="Дата поступления" filters={dates as any} setFilters={setDates as any} />
          <div className="w-[240px] min-h-[72px] flex flex-col">
            <label className={FIELD_LABEL}>Компания</label>
            <Select placeholder="Все" options={companyOptions} isVisible={false} customStyles={selectControlStyles} optionSelected={company} onSelected={setCompany} />
          </div>
          <div className="w-[220px] min-h-[72px] flex flex-col">
            <label className={FIELD_LABEL}>Модель</label>
            <Select placeholder="Все" options={modelOptions} isVisible={false} customStyles={selectControlStyles} optionSelected={model} onSelected={setModel} />
          </div>
          <div className="w-[240px] min-h-[72px] flex flex-col">
            <label className={FIELD_LABEL}>Тип</label>
            <Select placeholder="Все" options={typeOptions} isVisible={false} customStyles={selectControlStyles} optionSelected={type} onSelected={setType} />
          </div>
          <div className="w-[200px] min-h-[72px] flex flex-col">
            <label className={FIELD_LABEL}>Гарантия</label>
            <Select placeholder="Все" options={enumOptions(WARRANTY_LABELS)} isVisible={false} customStyles={selectControlStyles} optionSelected={warranty} onSelected={setWarranty} />
          </div>
          <div className="w-[200px] min-h-[72px] flex flex-col">
            <label className={FIELD_LABEL}>Брак</label>
            <Select placeholder="Все" options={enumOptions(DEFECT_LABELS)} isVisible={false} customStyles={selectControlStyles} optionSelected={defect} onSelected={setDefect} />
          </div>
          <div className="w-[200px] min-h-[72px] flex flex-col">
            <label className={FIELD_LABEL}>Виновник</label>
            <Select placeholder="Все" options={enumOptions(RESPONSIBLE_LABELS)} isVisible={false} customStyles={selectControlStyles} optionSelected={party} onSelected={setParty} />
          </div>
          <div className="w-[180px] min-h-[72px] flex flex-col">
            <label className={FIELD_LABEL}>Исход</label>
            <Select placeholder="Все" options={enumOptions(DISPOSITION_LABELS)} isVisible={false} customStyles={selectControlStyles} optionSelected={disposition} onSelected={setDisposition} />
          </div>
          <div className="w-[180px] min-h-[72px] flex flex-col">
            <label className={FIELD_LABEL}>Серийный №</label>
            <input
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
              placeholder="поиск"
              className="h-[44px] px-3 rounded-lg border border-gray-200 text-sm text-[#2d2c2c] focus:outline-none focus:border-gray-400"
            />
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Button onClick={apply}>Показать</Button>
            <Button isWhite onClick={resetAll}>Сбросить</Button>
            <Button isWhite onClick={() => { void exportXlsx(); }} disabled={exporting}>
              <span className="inline-flex items-center gap-1"><FiDownload /> {exporting ? 'Выгрузка…' : 'XLSX'}</span>
            </Button>
          </div>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-white rounded-xl shadow-sm p-4">
            <p className="text-xs text-gray-500">Всего устройств</p>
            <p className="text-2xl font-semibold text-[#2d2c2c]">{summary.total}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4">
            <p className="text-xs text-gray-500">Производственный брак</p>
            <p className="text-2xl font-semibold text-[#2d2c2c]">
              {summary.by_defect_type.find((x) => x.defect_type === 'production')?.n ?? 0}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4">
            <p className="text-xs text-gray-500">Гарантийных</p>
            <p className="text-2xl font-semibold text-[#2d2c2c]">
              {summary.by_warranty.find((x) => x.warranty_status === 'warranty')?.n ?? 0}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4">
            <p className="text-xs text-gray-500">Топ модель</p>
            <p className="text-base font-semibold text-[#2d2c2c] truncate" title={summary.by_model[0]?.model_name ?? ''}>
              {summary.by_model[0]?.model_name ?? '—'}
              {summary.by_model[0] ? <span className="text-gray-400 font-normal"> · {summary.by_model[0].n}</span> : null}
            </p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 overflow-x-auto">
          <DataTable<DiagnosticReportRow>
            columns={columns}
            data={rows}
            keyAccessor={(r) => r.id}
            onRowClick={canEdit ? (r) => { void openRow(r); } : undefined}
            loading={loading && rows.length === 0}
            emptyMessage="Нет данных по выбранным фильтрам"
          />
        </div>
        {count > PAGE_SIZE && (
          <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-gray-100">
            <button type="button" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1.5 rounded-md text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">‹ Назад</button>
            <span className="text-sm text-gray-500 px-2">Стр. {page} из {totalPages} · {count} строк</span>
            <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 rounded-md text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">Вперёд ›</button>
          </div>
        )}
      </div>

      <DeviceResultModal
        ticketId={editing?.ticketId ?? 0}
        device={editing?.device ?? null}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          load(applied, page); // перечитываем строки и сводку
        }}
      />
    </>
  );
}
